import { Component, OnInit, Input } from '@angular/core';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { Operateur } from 'src/app/data/intervenant/models/operateur.model';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { OperateurSitesService } from 'src/app/data/declaration/services/operateur-sites/operateur-sites.service';
import { HabilitationsService } from 'src/app/data/habilitation/services/habilitations/habilitations.service';
import { EChartsOption } from 'echarts';
import { Habilitation } from 'src/app/data/habilitation/models/habilitation.model';
import { UtilisateurTypeCode } from 'src/app/data/intervenant/models/enums/type-utilisateur.enum';
import { ManquementsService } from 'src/app/data/declaration/services/manquements/manquements.service';
import { RefCampagnesService } from 'src/app/data/declaration/services/ref-campagnes/ref-campagnes.service';
import { Campagne } from 'src/app/data/declaration/models/campagne.model';
import { OperateurInformationsDomaine } from 'src/app/data/intervenant/models/operateur-informations-domaine.model';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { ModificationOperateur } from '../../../../../data/intervenant/models/modification-operateur.model';
import { Organisme } from '../../../../../data/intervenant/models/organisme.model';
import { OrganismesService } from '../../../../../data/intervenant/services/organismes/organismes.service';
import { TypeModificationEnum } from '../../../../../data/intervenant/models/enums/type-modification.enum';
import { HistoriqueModification } from '../../../../../data/intervenant/models/historique-modification.model';

@Component({
    selector: 'app-operateur-dashboard',
    templateUrl: './operateur-dashboard.component.html',
    styleUrls: ['./operateur-dashboard.component.scss'],
    standalone: false
})
export class OperateurDashboardComponent implements OnInit {
    private getRouteParam(name: string): string | null {
        let current: ActivatedRoute | null = this.route;

        while (current) {
            const value = current.snapshot.paramMap.get(name);
            if (value !== null) {
                return value;
            }
            current = current.parent;
        }

        return null;
    }

    private get idOperateur(): number {
        const routeId = this.getRouteParam('idOperateur');
        return routeId ? Number(routeId) : SessionContext.get('idIntervenant');
    }

    operateur: Operateur;
    nbSites: number;

    estOperateur: boolean;
    chartHabilitation: EChartsOption;
    habilitations: Habilitation[];
    nbHabilites: number;
    nbEnCours: number;
    nbRefuses: number;
    nbRetires: number;
    nbManquements: number;
    campagneEnCours: Campagne;

    urlFacturationPanel: string | null = null;

    loadingOperateur = false;
    operateurLoadingError = false;

    /**
     * Number CVI
     */
    nbCVI: OperateurInformationsDomaine;

    /**
     * Constant code CVI
     */
    CODE_CVI = 'VIN_CVI';

    /**
     * List of modifications
     */
    modifications: HistoriqueModification[] = [];

    /**
     * Organisme
     */
    organismes: Organisme[] = [];

    @Input()
    options: any;

    /**
     * Type de modification enum
     */
    protected readonly TypeModificationEnum = TypeModificationEnum;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly loaderService: LoaderService,
        private readonly operateurService: OperateursService,
        private readonly sitesService: OperateurSitesService,
        private readonly refCampagnesService: RefCampagnesService,
        private readonly habilitationsService: HabilitationsService,
        private readonly manquementsService: ManquementsService,
        private readonly organismesService: OrganismesService
    ) {}

    ngOnInit() {
        this.estOperateur = SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR;

        if (!this.idOperateur) {
            this.router.navigate(['/main/operateurs/or/recherche-affilie']);
        }

        this.loadingOperateur = true;
        this.operateurLoadingError = false;
        forkJoin({
            operateur: this.operateurService.getOperateur(this.idOperateur),
            infosDomaine: this.operateurService.getInformationsDomaine(this.idOperateur),
            sites: this.sitesService.getSitesOperateur(this.idOperateur),
            habilitations:
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR
                    ? this.habilitationsService.getHabilitations()
                    : this.habilitationsService.getOperateurHabilitations(this.idOperateur),
            nbManquements: this.manquementsService.countManquementEncoursOperateur(this.idOperateur),
            campagne: this.refCampagnesService.getCampagneEnCours(),
            urlPanel:
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME
                    ? this.operateurService.getOperateursFacturePanel(this.idOperateur, SessionContext.get('idOrganisme'))
                    : of(''),
            modifications: this.operateurService.getHistoriqueModificationsOperateur(this.idOperateur, '', ''),
            organismes: this.organismesService.getOrganismes()
        })
            .pipe(
                this.loaderService.operator(),
                finalize(() => {
                    this.loadingOperateur = false;
                })
            )
            .subscribe(
                ({ operateur, infosDomaine, sites, habilitations, nbManquements, campagne, urlPanel, modifications, organismes }) => {
                    this.operateur = operateur;

                    if (sites) {
                        this.nbSites = sites.length;
                    } else {
                        this.nbSites = 0;
                    }
                    this.habilitations = habilitations;
                    this.loadHabilitations();
                    this.nbManquements = nbManquements;
                    this.campagneEnCours = campagne;
                    this.urlFacturationPanel = urlPanel;
                    this.nbCVI = (infosDomaine ?? []).find(info => info.code === this.CODE_CVI) ?? null;
                    this.modifications = modifications
                        .sort((a, b) => new Date(b.dateModification).getTime() - new Date(a.dateModification).getTime())
                        .slice(0, 3);
                    this.organismes = organismes;
                },
                () => {
                    this.operateurLoadingError = true;
                }
            );
    }

    loadHabilitations(): void {
        this.nbHabilites = this.habilitations.filter(habilitation => habilitation.statut && habilitation.statut.code === 'HABILITE').length;
        this.nbEnCours = this.habilitations.filter(habilitation => habilitation.statut && habilitation.statut.code === 'ENCOURS').length;
        this.nbRefuses = this.habilitations.filter(habilitation => habilitation.statut && habilitation.statut.code === 'REFUSE').length;
        this.nbRetires = this.habilitations.filter(habilitation => habilitation.statut && habilitation.statut.code === 'RETIRE').length;

        this.chartHabilitation = {
            tooltip: {
                trigger: 'item'
            },
            legend: {
                top: '5%',
                left: 'auto'
            },
            series: [
                {
                    name: 'Habilitations',
                    type: 'pie',
                    radius: ['30%', '60%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: {
                        show: false,
                        position: 'center'
                    },
                    emphasis: {
                        label: {
                            show: false,
                            fontSize: 40,
                            fontWeight: 'bold'
                        }
                    },
                    labelLine: {
                        show: false
                    },
                    data: [
                        { value: this.nbEnCours, name: 'En cours', itemStyle: { color: '#ac83d7' } },
                        { value: this.nbHabilites, name: 'Habilités', itemStyle: { color: '#a9d86e' } },
                        { value: this.nbRefuses, name: 'Refusés', itemStyle: { color: '#ff6c60' } },
                        { value: this.nbRetires, name: 'Retirés', itemStyle: { color: '#777777' } }
                    ]
                }
            ]
        };
    }

    goToDetail(): void {
        if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME) {
            this.router.navigate(['main/operateurs/or/' + this.operateur.id + '/informations']);
        } else {
            this.router.navigate(['main/operateurs/op/informations']);
        }
    }

    goToSynthese(): void {
        if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME) {
            this.router.navigate(['main/operateurs/or/' + this.operateur.id + '/synthese']);
        } else {
            this.router.navigate(['main/declarations/synthese']);
        }
    }

    goToHistorique(): void {
        if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME) {
            this.router.navigate(['main/operateurs/or/' + this.operateur.id + '/historique']);
        } else {
            this.router.navigate(['main/declarations/historique']);
        }
    }

    goToImport(): void {
        if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME) {
            this.router.navigate(['main/operateurs/or/' + this.operateur.id + '/dr/' + this.campagneEnCours.annee]);
        } else {
            this.router.navigate(['main/declarations/dr/' + this.campagneEnCours.annee]);
        }
    }

    goToFactures(): void {
        window.open(this.urlFacturationPanel, '_blank');
    }

    /**
     * Retourne le nom de l'organisme en fonction de son id intervenant
     * @param idOrganisme - Id de l'intervenant
     */
    getOrganisme(idOrganisme: number): string {
        return this.hasOrganisme(idOrganisme) ? this.organismes.find(o => o.id === idOrganisme).raisonSociale : '';
    }

    /**
     * Vrai si l'intervenant est associé à un organisme
     * @param idOrganisme - Id de l'intervenant
     */
    hasOrganisme(idOrganisme: number): boolean {
        return this.organismes.some(o => o.id === idOrganisme);
    }
    /**
     * Redirection vers la page des modifications
     */
    goToDetailModifications() {
        this.router.navigate(['/main/historique-modification/liste'], { state: { operateur: this.idOperateur } });
    }
}
